import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { MdArrowBack, MdVisibility, MdVisibilityOff, MdCheckCircle, MdCancel } from 'react-icons/md';
import { resetPassword } from '../redux/actions/authActions';
import { AlmataMainLogo } from '../../assets/image';

const ResetPassword = ({ onBackToLogin }) => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  
  const resetToken = searchParams.get('resetToken');
  const email = searchParams.get('email');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showRequirements, setShowRequirements] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  
  const { resetPasswordLoading, resetPasswordMessage, resetPasswordError } = useSelector(state => state.auth);
  const [success, setSuccess] = useState(false);

  const passwordRequirements = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)
  };

  const isPasswordValid = Object.values(passwordRequirements).every(req => req);

  useEffect(() => {
    if (resetPasswordMessage) {
      setSuccess(true);
    }
  }, [resetPasswordMessage]);

  const validatePassword = () => {
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    if (!isPasswordValid) {
      setPasswordError('Password does not meet all requirements');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validatePassword()) {
      dispatch(resetPassword(resetToken, email, newPassword));
    }
  };

  const RequirementItem = ({ met, text }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', marginBottom: '6px' }}>
      {met ? (
        <MdCheckCircle style={{ color: '#10b981', fontSize: '1.2rem' }} />
      ) : (
        <MdCancel style={{ color: '#ef4444', fontSize: '1.2rem' }} />
      )}
      <span style={{ color: met ? '#10b981' : '#6b7280' }}>{text}</span>
    </div>
  );

  if (success) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img 
              src={AlmataMainLogo}
              alt="Cyber Risk Report Logo" 
              className="login-logo"
            />
            <h1 className="login-title">Password Reset Successfully</h1>
            <p className="login-subtitle text-primary">{resetPasswordMessage}</p>
          </div>
          
          <button 
            onClick={onBackToLogin}
            className="back-to-login-btn"
          >
            <MdArrowBack />
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (!resetToken || !email) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img 
              src={AlmataMainLogo}
              alt="Cyber Risk Report Logo" 
              className="login-logo"
            />
            <h1 className="login-title">Invalid Link</h1>
            <p className="login-subtitle">The reset link is invalid or expired</p>
          </div>
          
          <button 
            onClick={onBackToLogin}
            className="back-to-login-btn"
          >
            <MdArrowBack />
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img 
            src={AlmataMainLogo}
            alt="Cyber Risk Report Logo" 
            className="login-logo"
          />
          <h1 className="login-title">Reset Password</h1>
          <p className="login-subtitle">Enter your new password</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group" style={{ position: 'relative' }}>
            <label htmlFor="newPassword">New Password</label>
            <div className="input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={() => setShowRequirements(true)}
                onBlur={() => setShowRequirements(false)}
                placeholder="Enter new password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
              </button>
            </div>
            
            {showRequirements && newPassword && (
              <div style={{ position: 'absolute', top: '-145px', left: 0, right: 0, marginTop: '12px', padding: '10px', backgroundColor: 'rgb(233 239 251)', borderRadius: '6px', zIndex: 9999, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
                <RequirementItem met={passwordRequirements.length} text="At least 8 characters" />
                <RequirementItem met={passwordRequirements.uppercase} text="One uppercase letter (A-Z)" />
                <RequirementItem met={passwordRequirements.number} text="One number (0-9)" />
                <RequirementItem met={passwordRequirements.special} text="One special character (!@#$%^&*)" />
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setConfirmPasswordTouched(true)}
                placeholder="Confirm password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
              </button>
            </div>
            {confirmPasswordTouched && confirmPassword && newPassword !== confirmPassword && (
              <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '6px' }}>
                Passwords do not match
              </div>
            )}
          </div>

          {(passwordError || resetPasswordError) && (
            <div className="error-message" style={{ color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {passwordError || resetPasswordError}
            </div>
          )}
          
          <button type="submit" className="login-btn" disabled={resetPasswordLoading || !isPasswordValid}>
            {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
          </button>
          
          <button 
            type="button"
            onClick={onBackToLogin}
            className="back-to-login-btn"
          >
            <MdArrowBack />
            Back to Login
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
